import { CheckCircle, Done, PowerOff as NatsOffIcon, Power as NatsOnIcon, Warning } from "@mui/icons-material"
import { Box, Tooltip } from "@mui/material"
import SelectorDialogBase from "./controls/base/SelectorDialogBase"
import { useStore } from "@priolo/jon"
import { FunctionComponent, useMemo, useState } from "react"
import { useTranslation } from "react-i18next"
import natsSo, { NATS_STATUS } from "stores/nats"
import valuesSo from "stores/values"
import { ServiceNATS } from "types/Value"
import dialogSo from "stores/layout/dialogStore"

const TOOLTIP_MAX = 25

interface Props {
	sx?: any
}

const NatsCnnIcon: FunctionComponent<Props> = ({
	sx,
}) => {
	// STORE
	const natsSa = useStore(natsSo)
	const valuesSa = useStore(valuesSo)

	// HOOKS
	const { t } = useTranslation()
	const [dialogOpen, setDialogOpen] = useState(false)
	const [filterText, setFilterText] = useState("")

	// RENDER
	const natsConn = t(`app.nats.cnn.${natsSa.status}`)
	const wrapServices = useMemo(() => Object.values(valuesSa.services)
		.map(service => ({
			service,
			isOnline: (Date.now() - service.last) < 5000
		}))
		.sort((s1, s2) => s1.service.code.localeCompare(s2.service.code)),
		[valuesSa.services]
	)
	const filteredServices = useMemo(() =>
		filterText
			? wrapServices.filter(w => w.service.code.toLowerCase().includes(filterText.toLowerCase()))
			: wrapServices,
		[wrapServices, filterText]
	)
	const colorOnline = useMemo(() => wrapServices.some(s => !s.isOnline) ? "secondary" : "success", [wrapServices])
	const tooltipServices = wrapServices.slice(0, TOOLTIP_MAX)
	const hasMore = wrapServices.length > TOOLTIP_MAX

	const icon = {
		[NATS_STATUS.UNKNOW]: <NatsOnIcon sx={[{ opacity: 0.5 }, sx]} />,
		[NATS_STATUS.OFFLINE]: <NatsOffIcon sx={sx} color="error" />,
		[NATS_STATUS.RETRY]: <NatsOffIcon sx={sx} color="warning" />,
		[NATS_STATUS.ONLINE]: <NatsOnIcon sx={sx} color={colorOnline as any} />,
	}[natsSa.status]

	return <>
		<Tooltip
			placement="bottom"
			title={<Box>
				{tooltipServices.map((wrapService) =>
					<ServiceRow key={wrapService.service.code}
						service={wrapService.service}
						isOnline={wrapService.isOnline}
					/>
				)}
				{hasMore && (
					<Box sx={{ fontSize: "11px", opacity: 0.7, mt: 0.5 }}>
						+{wrapServices.length - TOOLTIP_MAX} more...
					</Box>
				)}
				<Box sx={{ fontSize: "12px", fontWeight: 700, mt: 0.5 }}>
					{natsConn}
				</Box>
			</Box>}
		>
			<Box sx={{ display: "inline-flex", cursor: "pointer" }} onClick={() => setDialogOpen(true)}>
				{icon}
			</Box>
		</Tooltip>

		<SelectorDialogBase
			isOpen={dialogOpen}
			onClose={(item) => {
				if (!!item) {
					navigator.clipboard.writeText(item.service.code)
					dialogSo.dialogOpen({ modal: false, text: `COPIED TO CLIPBOARD`, })
				}
				setDialogOpen(false)
			}}
			title={`Services (${wrapServices.length}) — ${natsConn}`}
			items={filteredServices}
			idSelect={null}
			textValue={filterText}
			onChangeTextValue={setFilterText}
			fnIdFromItem={w => w.service.code}
			fnTextFromItem={w => w.service.code}
			fnSecondaryFromItem={w => <ServiceSecondary service={w.service} isOnline={w.isOnline} />}
			width={350}
		/>
	</>
}

export default NatsCnnIcon


interface PropsServiceRow {
	service: ServiceNATS
	isOnline?: boolean
}

const ServiceRow: FunctionComponent<PropsServiceRow> = ({
	service,
	isOnline,
}) => {
	const lastTime = service.last
		? new Date(service.last).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit", second: "2-digit" })
		: "--:--:--"

	// RENDER
	return (
		<Box sx={cssRoot}>
			<Box sx={cssTitle}>
				{service.code}
			</Box>
			<Box sx={cssTime}>{lastTime}</Box>
			<Box>{isOnline ? <Done /> : <Warning />}</Box>
		</Box>
	)
}

const cssRoot = {
	maxWidth: '200px',
	display: 'flex',
	alignItems: 'center',
	gap: '6px',
}

const cssTitle = {
	flex: '1',
	overflow: 'hidden',
	textOverflow: 'ellipsis',
	whiteSpace: 'nowrap'
}

const cssTime = {
	fontSize: '11px',
	opacity: 0.6,
	whiteSpace: 'nowrap',
}


interface PropsServiceSecondary {
	service: ServiceNATS
	isOnline?: boolean
}

const ServiceSecondary: FunctionComponent<PropsServiceSecondary> = ({ service, isOnline }) => {
	const lastTime = service.last
		? new Date(service.last).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit", second: "2-digit" })
		: "--:--:--"
	return (
		<Box sx={{ display: "flex", alignItems: "center", gap: "6px" }}>
			{isOnline ? <CheckCircle sx={{ fontSize: "14px" }} color="success" /> : <Warning sx={{ fontSize: "14px" }} color="warning" />}
			<Box component="span" sx={{ fontSize: "11px", opacity: .7 }}>{lastTime}</Box>
		</Box>
	)
}